import React from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Divider,
  Button,
  Alert,
  Grid,
  Card,
  CardContent,
  CardHeader
} from '@mui/material';
import { useNotificationSettings } from '../../contexts/NotificationSettingsContext';
import { t } from '../../utils/textUtils';
import NotificationsIcon from '@mui/icons-material/Notifications';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import RestoreIcon from '@mui/icons-material/Restore';

const NotificationPollingSettings = () => {
  const { settings, updateSettings, resetSettings, intervalOptions } = useNotificationSettings();

  const handleToggleEnabled = (event) => {
    updateSettings({ enabled: event.target.checked });
  };

  const handleIntervalChange = (event) => {
    updateSettings({ interval: event.target.value });
  };

  const handleToggleSound = (event) => {
    updateSettings({ sound: event.target.checked });
  };

  const handleToggleDesktop = (event) => {
    updateSettings({ desktop: event.target.checked });
  };

  const handleToggleAutoMarkRead = (event) => {
    updateSettings({ autoMarkRead: event.target.checked });
  };

  const handleReset = () => {
    resetSettings();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        {t('PENGATURAN NOTIFIKASI OTOMATIS')}
      </Typography>

      <Grid container spacing={3}>
        {/* Main Settings Card */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader
              avatar={<NotificationsIcon color="primary" />}
              title={t('PENGATURAN POLLING NOTIFIKASI')}
              subheader={t('ATUR BAGAIMANA SISTEM MEMERIKSA NOTIFIKASI BARU')}
            />
            <CardContent>
              {/* Enable/Disable Notifications */}
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.enabled}
                      onChange={handleToggleEnabled}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {t('AKTIFKAN PEMBARUAN OTOMATIS')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {settings.enabled 
                          ? t('NOTIFIKASI AKAN DIPERBARUI SECARA OTOMATIS') 
                          : t('NOTIFIKASI TIDAK AKAN DIPERBARUI OTOMATIS')
                        }
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Polling Interval */}
              <Box sx={{ mb: 3 }}>
                <FormControl fullWidth disabled={!settings.enabled}>
                  <InputLabel>{t('INTERVAL PEMBARUAN')}</InputLabel>
                  <Select
                    value={settings.interval}
                    label={t('INTERVAL PEMBARUAN')}
                    onChange={handleIntervalChange}
                  >
                    {intervalOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('SEBERAPA SERING SISTEM AKAN MEMERIKSA NOTIFIKASI BARU. DEFAULT 5 MENIT MEMBERIKAN KESEIMBANGAN YANG BAIK ANTARA RESPONSIVITAS DAN EFISIENSI.')}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Sound Notifications */}
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.sound}
                      onChange={handleToggleSound}
                      disabled={!settings.enabled}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <VolumeUpIcon fontSize="small" />
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {t('SUARA NOTIFIKASI')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('PUTAR SUARA SAAT ADA NOTIFIKASI BARU (FITUR AKAN DATANG)')}
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </Box>

              {/* Desktop Notifications */}
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.desktop}
                      onChange={handleToggleDesktop}
                      disabled={!settings.enabled}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DesktopWindowsIcon fontSize="small" />
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {t('NOTIFIKASI DESKTOP')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('TAMPILKAN NOTIFIKASI DESKTOP (FITUR AKAN DATANG)')}
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </Box>

              {/* Auto Mark as Read */}
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.autoMarkRead}
                      onChange={handleToggleAutoMarkRead}
                      disabled={!settings.enabled}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AutoModeIcon fontSize="small" />
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {t('TANDAI OTOMATIS SUDAH DIBACA')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('OTOMATIS TANDAI NOTIFIKASI SEBAGAI SUDAH DIBACA SAAT DIKLIK')}
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </Box>

              {/* Reset Button */}
              <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Button
                  variant="outlined"
                  startIcon={<RestoreIcon />}
                  onClick={handleReset}
                  color="secondary"
                >
                  {t('RESET KE DEFAULT (5 MENIT)')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Status Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader
              title={t('STATUS SAAT INI')}
              subheader={t('INFORMASI PENGATURAN AKTIF')}
            />
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('STATUS NOTIFIKASI')}
                </Typography>
                <Typography variant="body1" fontWeight="medium" color={settings.enabled ? 'success.main' : 'error.main'}>
                  {settings.enabled ? t('AKTIF') : t('NON-AKTIF')}
                </Typography>
              </Box>

              {settings.enabled && (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('INTERVAL PEMBARUAN')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {intervalOptions.find(opt => opt.value === settings.interval)?.label || 'Unknown'}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Fitur Aktif
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {settings.sound && (
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <VolumeUpIcon fontSize="small" color="primary" />
                          Suara
                        </Typography>
                      )}
                      {settings.desktop && (
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <DesktopWindowsIcon fontSize="small" color="primary" />
                          Desktop
                        </Typography>
                      )}
                      {settings.autoMarkRead && (
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <AutoModeIcon fontSize="small" color="primary" />
                          Auto Mark Read
                        </Typography>
                      )}
                      {!settings.sound && !settings.desktop && !settings.autoMarkRead && (
                        <Typography variant="body2" color="text.secondary">
                          Hanya notifikasi dasar
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </>
              )}

              <Alert severity={settings.enabled ? "success" : "warning"} sx={{ mt: 2 }}>
                {settings.enabled 
                  ? `Notifikasi akan diperbarui setiap ${intervalOptions.find(opt => opt.value === settings.interval)?.label.toLowerCase()}`
                  : 'Notifikasi tidak akan diperbarui otomatis. Anda perlu refresh halaman manual.'
                }
              </Alert>
            </CardContent>
          </Card>

          {/* Performance Tips */}
          <Card sx={{ mt: 2 }}>
            <CardHeader
              title="TIPS PERFORMA"
              subheader="REKOMENDASI PENGGUNAAN"
            />
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                • <strong>10-30 DETIK:</strong> UNTUK MONITORING AKTIF
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                • <strong>1-2 MENIT:</strong> UNTUK PENGGUNAAN NORMAL
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                • <strong>5-10 MENIT:</strong> UNTUK MENGHEMAT BANDWIDTH
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • <strong>NON-AKTIF:</strong> JIKA TIDAK PERLU NOTIFIKASI REAL-TIME
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NotificationPollingSettings;